import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// =============================================================
// Configuración de pasarelas de pago (ePayco)
// =============================================================
// Las credenciales viven en la tabla `gateways_config`, con RLS exclusivo del
// rol Admin. La tienda pública SOLO lee la vista `gateways_config_public`, que
// expone `public_key`, `p_cust_id`, `is_test_mode` y `enabled` (nunca las
// llaves privadas `p_key` / `private_key`, que solo usan las Edge Functions).

export interface EpaycoPublicConfig {
  publicKey: string;
  pCustId: string;
  isTestMode: boolean;
  enabled: boolean;
  configured: boolean;
  checkoutDisplay: EpaycoCheckoutDisplay;
}

export interface EpaycoAdminConfig {
  publicKey: string;
  pCustId: string;
  pKey: string;
  privateKey: string;
  isTestMode: boolean;
  enabled: boolean;
  checkoutDisplay: EpaycoCheckoutDisplay;
}

// Forma en que ePayco presenta el checkout al cliente:
// - 'standard': ePayco abre su checkout como PÁGINA COMPLETA (external: "true").
//   Es el modo más confiable porque NO usa el modal/iframe que se bloquea en
//   entornos embebidos (p. ej. el previsualizador de Readdy).
// - 'onpage': modal flotante dentro de la misma página (checkout.js).
// - 'popup': se abre una pestaña externa con nuestra página segura.
export type EpaycoCheckoutDisplay = 'standard' | 'onpage' | 'popup';

const EMPTY_PUBLIC: EpaycoPublicConfig = {
  publicKey: '',
  pCustId: '',
  isTestMode: true,
  enabled: false,
  configured: false,
  checkoutDisplay: 'standard',
};

export function normalizeCheckoutDisplay(value: unknown): EpaycoCheckoutDisplay {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === 'onpage') return 'onpage';
  if (v === 'popup') return 'popup';
  return 'standard';
}

// URL pública del webhook/IPN de ePayco (Edge Function). Se inyecta en el
// checkout para que ePayco notifique el resultado de la transacción.
export function getEpaycoWebhookUrl(): string {
  const base = (import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '').replace(/\/+$/, '');
  if (!base) return '';
  return `${base}/functions/v1/epayco-webhook`;
}

// URL (misma app) a la que ePayco devuelve al cliente tras pagar. Aquí se
// verifica la transacción del lado del servidor.
export function getEpaycoResponseUrl(): string {
  const basePath = (__BASE_PATH__ || '').replace(/\/+$/, '');
  return `${window.location.origin}${basePath}/checkout/confirmacion`;
}

// URL de la página intermedia que abre el checkout de ePayco. En modo 'popup'
// se abre en una pestaña nueva; en modo 'onpage' también sirve como respaldo.
export function getEpaycoPayPageUrl(): string {
  const basePath = (__BASE_PATH__ || '').replace(/\/+$/, '');
  return `${window.location.origin}${basePath}/checkout/epayco`;
}

// Datos necesarios para iniciar el pago de ePayco sin exponer llaves. Se pasan
// a la página de pago (por estado de navegación o postMessage), y allí se
// combinan con la configuración pública (public_key / modo pruebas).
export interface EpaycoPayPayload {
  orderNumber: string;
  orderId: string;
  amount: number;
  description: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  customerDocument: string;
}

// Consulta la configuración pública de ePayco (segura para la tienda). Solo
// lee columnas no sensibles a través de la vista `gateways_config_public`.
export async function getEpaycoPublicConfig(): Promise<EpaycoPublicConfig> {
  if (!isSupabaseConfigured || !supabase) return { ...EMPTY_PUBLIC };
  try {
    const { data, error } = await supabase
      .from('gateways_config_public')
      .select('public_key, p_cust_id, is_test_mode, enabled, checkout_display')
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...EMPTY_PUBLIC };
    const row = data as Record<string, unknown>;
    const publicKey = String(row.public_key ?? '').trim();
    const pCustId = String(row.p_cust_id ?? '').trim();
    const enabled = row.enabled === undefined || row.enabled === null ? true : Boolean(row.enabled);
    const isTestMode =
      row.is_test_mode === undefined || row.is_test_mode === null ? true : Boolean(row.is_test_mode);
    return {
      publicKey,
      pCustId,
      isTestMode,
      enabled,
      configured: Boolean(publicKey && pCustId) && enabled,
      checkoutDisplay: normalizeCheckoutDisplay(row.checkout_display),
    };
  } catch (err) {
    console.warn('[gateways] getEpaycoPublicConfig failed:', err);
    return { ...EMPTY_PUBLIC };
  }
}

// ⚠️ SOLO ADMINISTRADOR: lee la configuración completa (incluidas las llaves
// privadas) para el panel de administración. La protección real la aplica RLS
// (is_admin()); nunca llames a esta función desde la tienda pública.
export async function fetchEpaycoAdminConfig(): Promise<EpaycoAdminConfig> {
  const empty: EpaycoAdminConfig = {
    publicKey: '',
    pCustId: '',
    pKey: '',
    privateKey: '',
    isTestMode: true,
    enabled: true,
    checkoutDisplay: 'standard',
  };
  if (!isSupabaseConfigured || !supabase) return empty;
  const { data, error } = await supabase
    .from('gateways_config')
    .select('public_key, p_cust_id, p_key, private_key, is_test_mode, enabled, checkout_display')
    .limit(1)
    .maybeSingle();
  if (error || !data) return empty;
  const row = data as Record<string, unknown>;
  return {
    publicKey: String(row.public_key ?? ''),
    pCustId: String(row.p_cust_id ?? ''),
    pKey: String(row.p_key ?? ''),
    privateKey: String(row.private_key ?? ''),
    isTestMode: row.is_test_mode === undefined || row.is_test_mode === null ? true : Boolean(row.is_test_mode),
    enabled: row.enabled === undefined || row.enabled === null ? true : Boolean(row.enabled),
    checkoutDisplay: normalizeCheckoutDisplay(row.checkout_display),
  };
}

// Guarda (upsert) la configuración de ePayco desde el panel de administración.
export async function saveEpaycoAdminConfig(patch: Partial<EpaycoAdminConfig>): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase no está configurado');
  }
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.publicKey !== undefined) row.public_key = patch.publicKey.trim();
  if (patch.pCustId !== undefined) row.p_cust_id = patch.pCustId.trim();
  if (patch.pKey !== undefined) row.p_key = patch.pKey.trim();
  if (patch.privateKey !== undefined) row.private_key = patch.privateKey.trim();
  if (patch.isTestMode !== undefined) row.is_test_mode = patch.isTestMode;
  if (patch.enabled !== undefined) row.enabled = patch.enabled;
  if (patch.checkoutDisplay !== undefined) row.checkout_display = patch.checkoutDisplay;

  const { data: existing } = await supabase
    .from('gateways_config')
    .select('id')
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase.from('gateways_config').update(row).eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase.from('gateways_config').insert(row);
    if (error) throw new Error(error.message);
  }
}

// =============================================================
// Checkout ePayco (checkout.js oficial)
// =============================================================

const EPAYCO_SCRIPT_URL = 'https://checkout.epayco.co/checkout.js';
const EPAYCO_SCRIPT_ID = 'epayco-checkout-script';

interface EpaycoCheckoutData {
  name: string;
  description: string;
  invoice: string;
  currency: string;
  amount: string;
  tax_base: string;
  tax: string;
  country: string;
  lang: string;
  external: string;
  confirmation: string;
  response: string;
  email_billing: string;
  name_billing: string;
  address_billing: string;
  type_doc_billing: string;
  number_doc_billing: string;
  mobilephone_billing: string;
  extra1: string;
}

interface EpaycoHandler {
  open: (data: EpaycoCheckoutData) => void;
}

interface EpaycoGlobal {
  checkout: {
    configure: (opts: { key: string; test: boolean }) => EpaycoHandler;
    open?: (data: EpaycoCheckoutData) => void;
  };
}

// Carga el script oficial de ePayco una sola vez. Resuelve cuando el objeto
// global `ePayco` está disponible en el navegador.
export function isEpaycoLoaded(): boolean {
  const global = window as unknown as { ePayco?: unknown };
  return Boolean(global.ePayco);
}

// Carga el script oficial de ePayco una sola vez. Resuelve cuando el objeto
// global `ePayco` está disponible en el navegador.
export function loadEpaycoScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const global = window as unknown as { ePayco?: unknown };
    if (global.ePayco) {
      resolve();
      return;
    }
    const existing = document.getElementById(EPAYCO_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar ePayco.')));
      return;
    }
    const script = document.createElement('script');
    script.id = EPAYCO_SCRIPT_ID;
    script.src = EPAYCO_SCRIPT_URL;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar ePayco.'));
    document.head.appendChild(script);
  });
}

export interface OpenEpaycoParams {
  publicKey: string;
  isTestMode: boolean;
  orderNumber: string;
  orderId: string;
  amount: number;
  description: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  customerDocument: string;
  confirmationUrl: string;
  responseUrl: string;
  // Modo de presentación deseado. En 'standard' ePayco abre su checkout como
  // página completa (external: "true"); en el resto usa el modal onpage.
  display?: EpaycoCheckoutDisplay;
}

// Abre la ventana de pago (onpage/standard) del Checkout ePayco. Usa
// ÚNICAMENTE la PUBLIC_KEY y el P_CUST_ID cargados desde la configuración; las
// llaves privadas jamás llegan al navegador.
export function openEpaycoCheckout(params: OpenEpaycoParams): void {
  const global = window as unknown as { ePayco?: EpaycoGlobal };
  const ePayco = global.ePayco;
  if (!ePayco || !ePayco.checkout) {
    throw new Error('El Checkout de ePayco aún no está disponible. Inténtalo de nuevo.');
  }
  const amount = String(Math.round(params.amount));
  const data: EpaycoCheckoutData = {
    name: params.description,
    description: params.description,
    invoice: params.orderNumber,
    currency: 'COP',
    amount,
    tax_base: amount,
    tax: '0',
    country: 'co',
    lang: 'es',
    // `external: "true"` hace que ePayco abra su checkout como página completa
    // (modo estándar). En los demás modos se usa el modal onpage.
    external: params.display === 'standard' ? 'true' : 'false',
    confirmation: params.confirmationUrl,
    response: params.responseUrl,
    email_billing: params.customerEmail,
    name_billing: params.customerName,
    address_billing: params.customerAddress,
    type_doc_billing: 'CC',
    number_doc_billing: params.customerDocument,
    mobilephone_billing: params.customerPhone,
    extra1: params.orderId,
  };
  const handler = ePayco.checkout.configure({ key: params.publicKey, test: params.isTestMode });
  if (handler && typeof handler.open === 'function') {
    handler.open(data);
  } else if (typeof ePayco.checkout.open === 'function') {
    ePayco.checkout.open(data);
  } else {
    throw new Error('No se pudo iniciar el Checkout de ePayco.');
  }
}

export type EpaycoResponseStatus = 'APPROVED' | 'DECLINED' | 'PENDING' | 'ERROR';

export interface EpaycoVerifyResult {
  status: EpaycoResponseStatus;
  orderNumber: string;
  total: number;
  updated: boolean;
}

// Verifica el resultado de la transacción ePayco (página de respuesta) a
// través de la Edge Function `verify-epayco-transaction`, que valida la firma
// y aprueba el pedido del lado del servidor.
export async function verifyEpaycoTransaction(
  params: Record<string, string>,
): Promise<EpaycoVerifyResult> {
  if (!supabase) throw new Error('Supabase no está configurado');
  const { data, error } = await supabase.functions.invoke('verify-epayco-transaction', {
    body: { params },
  });
  if (error) throw new Error(error.message ?? 'No se pudo verificar el pago.');
  if (data?.error) throw new Error(data.error);
  return data as EpaycoVerifyResult;
}